// @givmo/app-admin — shared confirm dialog.
//
// A controlled confirm dialog over the design-system <Dialog> primitive, gating a destructive
// mutation behind a Cancel / Confirm choice (ports the Flutter `PopUpTemplate` confirm flow used by
// the employees Remove pop-up). Built on the raw <Dialog> parts (not <PopUpTemplate>) so the footer
// carries BOTH a Cancel and a Confirm button — PopUpTemplate has a single primary footer button.
// Base UI gives the accessible plumbing (focus trap/restore, Escape + outside-click dismiss, scrim).
// The Confirm button is a real kit <Button> that shows a spinner while the mutation is in flight.
//
// `title`/`body` are plain strings; `subject` renders an OPTIONAL centered line between them (the
// employees dialog shows the employee name or "{n} employees selected" there). `note` is an OPTIONAL
// second paragraph of the body, in the body's own style and inside the same description, so a screen
// reader announces it with the warning (the employees dialog says pending match requests are
// cancelled there, CAM-R38).

import { Button, Dialog, testIdProp } from '@givmo/design-system';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Optional centered subject line shown between the title and the body warning. */
  subject?: string;
  body: string;
  /** Optional second paragraph of the body, same style, announced with it. */
  note?: string;
  confirmLabel: string;
  /** Magenta (destructive) confirm button vs the default blue. */
  destructive?: boolean;
  /** Confirm button busy state (the mutation is in flight). */
  loading?: boolean;
  onConfirm: () => void;
  testId: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  subject,
  body,
  note,
  confirmLabel,
  destructive = false,
  loading = false,
  onConfirm,
  testId,
}: ConfirmDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content aria-label={title} className="flex flex-col gap-12" testId={testId}>
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>

        {subject !== undefined && (
          <p
            className="font-sans text-s14 font-w600 tracking-givmo text-text-strong"
            {...testIdProp(`${testId}-subject`)}
          >
            {subject}
          </p>
        )}

        <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {body}
          {note !== undefined && (
            <span className="mt-8 block" {...testIdProp(`${testId}-note`)}>
              {note}
            </span>
          )}
        </Dialog.Description>

        <div className="mt-12 flex items-center justify-end gap-8">
          <Dialog.Close
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp(`${testId}-cancel`)}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone={destructive ? 'danger' : 'primary'}
            size="md"
            loading={loading}
            onClick={onConfirm}
            testId={`${testId}-confirm`}
          >
            {confirmLabel}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
